import { useState, useEffect } from 'react'
import { fetchGallery } from '../../services/api'

const Moments = () => {
  const [selectedImage, setSelectedImage] = useState(null)
  const [dbGallery, setDbGallery] = useState([])

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setSelectedImage(null)
    }
    if (selectedImage) {
      window.addEventListener('keydown', handleKeyDown)
    }
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [selectedImage])

  useEffect(() => {
    fetchGallery().then((data) => {
      if (data && data.length > 0) {
        setDbGallery(data)
      }
    })
  }, [])

  const defaultBentoItems = [
    { img: "/gallery/1.png", label: "Rescue & Shelter" },
    {
      type: "text",
      title: "Rescuing Lives Daily",
      color: "text-brand-orange-700"
    },
    { img: "/gallery/2.png", label: "Daily Feedings" },
    {
      type: "text",
      title: "Rabies-Free Bengaluru",
      color: "text-slate-800"
    },
    { img: "/gallery/3.png", label: "Puppy Care" },
    { img: "/gallery/4.png", label: "Medical Squad" },
    { img: "/gallery/5.png", label: "Sanctuary Rest" },
    {
      type: "text",
      title: "Be Their Voice",
      color: "text-brand-orange-700"
    },
    { img: "/gallery/6.png", label: "Adoption Smiles" },
    {
      type: "text",
      title: "Adopt, Don't Shop",
      color: "text-emerald-700"
    },
    { img: "/gallery/7.png", label: "Volunteer Love" },
    { img: "/gallery/8.png", label: "Health Checkup" },
    { img: "/gallery/9.png", label: "Happy Tails" },
    { img: "/gallery/10.png", label: "Community Outreach" },
    { img: "/gallery/1.png", label: "Emergency Ambulance" },
    { img: "/gallery/2.png", label: "Sterilization Camp" },
  ]

  const bentoLayouts = [
    { span: "col-span-1", height: "h-48 sm:h-56 2xl:h-64" },
    { span: "col-span-1 sm:col-span-2", height: "h-48 sm:h-56 2xl:h-64" },
    { span: "col-span-1", height: "h-48 sm:h-56 2xl:h-64" },
    { span: "col-span-1", height: "h-48 sm:h-56 2xl:h-64" },
    { span: "col-span-1", height: "h-48 sm:h-56 2xl:h-64" },
    { span: "col-span-1 sm:col-span-2", height: "h-48 sm:h-56 2xl:h-64" },
    { span: "col-span-1 sm:col-span-2", height: "h-48 sm:h-56 2xl:h-64" },
    { span: "col-span-1", height: "h-48 sm:h-56 2xl:h-64" },
    { span: "col-span-1", height: "h-48 sm:h-56 2xl:h-64" },
    { span: "col-span-1", height: "h-48 sm:h-56 2xl:h-64" },
    { span: "col-span-1", height: "h-48 sm:h-56 2xl:h-64" },
    { span: "col-span-1", height: "h-48 sm:h-56 2xl:h-64" },
    { span: "col-span-1", height: "h-48 sm:h-56 2xl:h-64" },
    { span: "col-span-1", height: "h-48 sm:h-56 2xl:h-64" },
    { span: "col-span-1 sm:col-span-2", height: "h-48 sm:h-56 2xl:h-64" },
    { span: "col-span-1", height: "h-48 sm:h-56 2xl:h-64" },
  ]

  // Dynamic gallery merging with DB images
  const rawList = dbGallery.length > 0
    ? [...dbGallery.map(g => ({ img: g.image_url, label: g.title || "Shelter Moment" })), ...defaultBentoItems]
    : defaultBentoItems

  const bentoList = rawList.map((item, idx) => ({
    ...item,
    ...bentoLayouts[idx % bentoLayouts.length]
  }))

  const renderBentoCard = (item, idx, blockId) => {
    const isTextCard = item.type === "text"

    if (isTextCard) {
      return (
        <div
          key={`bento-${blockId}-${idx}`}
          className={`${item.span} ${item.height} relative shrink-0 bg-transparent flex items-center justify-start text-left select-none p-2 sm:p-4`}
        >
          <h3
            className={`text-2xl sm:text-3xl lg:text-4xl 2xl:text-5xl font-handwritten font-bold leading-snug tracking-wide ${item.color || 'text-brand-orange-700'} w-full text-left`}
            style={{ fontFamily: "'Jumpswinter', cursive, sans-serif" }}
          >
            {item.title}
          </h3>
        </div>
      )
    }

    return (
      <div
        key={`bento-${blockId}-${idx}`}
        onClick={() => setSelectedImage(item.img)}
        className={`${item.span} ${item.height} rounded-2xl relative overflow-hidden shrink-0 bg-transparent cursor-pointer group`}
      >
        {/* Background Image */}
        <img
          src={item.img}
          alt={`CAWT Moment ${idx + 1}`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 rounded-2xl"
        />
      </div>
    )
  }

  return (
    <section
      id="gallery"
      className="relative bg-[#FAF6F2] text-slate-800 pt-10 sm:pt-14 2xl:pt-18 pb-10 sm:pb-14 select-none overflow-hidden"
    >
      {/* Self-contained high performance vertical marquee animation styles */}
      <style>{`
        @keyframes bento-marquee-glide-up {
          0% { transform: translate3d(0, 0, 0); }
          100% { transform: translate3d(0, -50%, 0); }
        }
        .animate-bento-marquee-up {
          animation: bento-marquee-glide-up 40s linear infinite;
        }
      `}</style>

      {/* Header Block: Title on Left End, Description on Right End */}
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 2xl:px-16 mb-6 sm:mb-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          {/* Left End: Badge & Heading */}
          <div className="space-y-1.5 text-left">
            <span className="inline-block px-3.5 py-1 bg-brand-orange-50 border border-brand-orange-100 text-brand-orange-700 font-bold text-xs uppercase rounded-full tracking-widest">
              SHELTER DIARIES
            </span>
            <h2 className="text-3xl xs:text-4xl sm:text-5xl 2xl:text-6xl text-slate-850 font-handwritten font-bold tracking-wide leading-tight w-full">
              Moments at{' '}
              <span className="text-brand-orange-700 font-normal inline sm:inline-block" style={{ fontFamily: "'Jumpswinter', sans-serif" }}>
                Chetak Animal Welfare Trust
              </span>
            </h2>
          </div>

          {/* Right End: Description */}
          <div className="md:text-right max-w-md 2xl:max-w-lg">
            <p className="text-slate-500 text-xs sm:text-sm 2xl:text-base font-semibold leading-relaxed">
              A visual journey of rescues, adoptions, and daily sanctuary life. Click any photo to expand.
            </p>
          </div>
        </div>
      </div>

      {/* Full-width Bento Grid Vertical Marquee Container */}
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 2xl:px-16">
        <div className="relative h-[540px] sm:h-[620px] 2xl:h-[720px] overflow-hidden rounded-3xl z-10">

          {/* Scrolling Vertical Wrapper */}
          <div className="flex flex-col gap-4 sm:gap-6 animate-bento-marquee-up hover:[animation-play-state:paused] cursor-grab active:cursor-grabbing">

            {/* Bento Grid Block 1 (Single column on mobile, 3 on sm, 4 on lg) */}
            <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6 w-full">
              {bentoList.map((item, idx) => renderBentoCard(item, idx, '1'))}
            </div>

            {/* Bento Grid Block 2 (Duplicate for Seamless Infinite Loop) */}
            <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6 w-full">
              {bentoList.map((item, idx) => renderBentoCard(item, idx, '2'))}
            </div>

          </div>
        </div>
      </div>

      {/* Lightbox Photo Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-md p-4 sm:p-8 transition-opacity duration-300"
          onClick={() => setSelectedImage(null)}
        >
          <div
            className="relative max-w-5xl max-h-[90vh] w-full flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-12 right-0 sm:top-2 sm:right-2 text-white/80 hover:text-white bg-black/60 hover:bg-black/90 rounded-full p-2.5 transition-all shadow-lg z-50 border border-white/20"
              aria-label="Close photo preview"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Enlarged Image */}
            <img
              src={selectedImage}
              alt="Enlarged moment preview"
              className="max-w-full max-h-[85vh] object-contain shadow-2xl rounded-xl border border-white/10"
            />
          </div>
        </div>
      )}
    </section>
  )
}

export default Moments

